import { Form } from 'antd'
import { bool, func, object, string } from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import FormFactory from 'components/formFactory'
import ContractPreview from 'components/contractPreview'
import { ExpandableDescription } from './components/ExpandableDescription'
import { appendAnswer } from '~/states/modules/answer'
import { nextPage, previousPage } from 'states/modules/question'

import {
	FormLayout,
	FormHeader,
	FormButton,
	FormContent,
	FormContentContainer,
	FormFooter,
	FormPagination,
	SuggestionAlert,
} from './styles'

const DocumentForm = ({
	initialValues = {},
	onFinish,
	cancelRoute,
	allowDraft = true,
	hasPreview = false,
	isFinishing = false,
	isExternal = false,
	templateId = null,
}) => {
	const dispatch = useDispatch()

	const [form] = Form.useForm()
	const [requesterData, setRequesterData] = useState(null)
	const history = useHistory()

	// get the questions from the redux store
	const { questions, lastPage, title } = useSelector(
		({ question }) => ({
			questions: question.data,
			lastPage: hasPreview ? question.lastPage + 1 : question.lastPage,
			title: question.templateName,
		}),
		shallowEqual
	)
	const template = useSelector(({ editTemplate }) => editTemplate.data)
	const visible = useSelector(({ question }) => question.visible)
	const currentPage = useSelector(({ question }) => question.currentPage)
	const isLastPage = currentPage === lastPage

	const getVisiblePages = useCallback(
		(visible, currentPage) => {
			// Create an array of visible pages
			const parsedVisiblePages = hasPreview ? [...visible, [true]] : visible
			const visiblePages = parsedVisiblePages
				.map((page, index) => (page.some(Boolean) ? index : null))
				.filter((page) => page !== null)

			// Find the index of the current page within the visible pages
			const currentStepper = visiblePages.findIndex(
				(page) => page === currentPage
			)

			return {
				currentStepper,
				totalPages: visiblePages.length,
			}
		},
		[hasPreview]
	)

	const { currentStepper, totalPages } = getVisiblePages(visible, currentPage)

	// page navigation functions
	const onPrevious = () => {
		dispatch(previousPage())
	}

	const onNext = async () => {
		form
			.validateFields()
			.then(() => {
				if ((hasPreview && !isLastPage) || !hasPreview) {
					dispatch(
						appendAnswer({
							data: form.getFieldsValue(),
							pageFieldsData: questions[currentPage],
							visible: visible[currentPage],
						})
					)
				}
				if (!isLastPage) {
					dispatch(nextPage({ hasPreview }))
				}
			})
			.catch((e) => console.log(e))
	}

	// append the answers to the redux store
	useEffect(() => {
		if (questions.length > 0 && ((hasPreview && !isLastPage) || !hasPreview)) {
			dispatch(
				appendAnswer({
					data: form.getFieldsValue(),
					pageFieldsData: questions[currentPage],
					visible: visible[currentPage],
				})
			)
		}
		// eslint-disable-next-line
	}, [questions])

	const onSubmit = (data, draft = false) => {
		if ((hasPreview && !isLastPage) || !hasPreview) {
			dispatch(
				appendAnswer({
					data,
					pageFieldsData: questions[currentPage],
					visible: visible[currentPage],
				})
			)
		}
		if (isLastPage || draft) {
			onFinish(draft, history)
		}
	}

	function handleGoTo(path) {
		return history.push(path)
	}

	const ExtraButtons = () => {
		const buttons = []

		buttons[1] = (
			<FormButton
				key="1"
				onClick={() => {
					form
						.validateFields()
						.then(() => {
							onSubmit(form.getFieldsValue())
						})
						.catch((e) => console.log(e))
				}}
				loading={isFinishing}
				disabled={!isLastPage || isFinishing}
				ghost={!isLastPage}
				type="primary"
				htmlType="submit">
				Salvar
			</FormButton>
		)

		if (allowDraft) {
			buttons[0] = (
				<FormButton
					type="primary"
					key="2"
					onClick={() => {
						onSubmit(form.getFieldsValue(), true)
					}}>
					Rascunho
				</FormButton>
			)
		}

		return buttons
	}

	return (
		<FormLayout>
			<FormHeader
				title={title}
				extra={allowDraft ? ExtraButtons()[0] : null}
				onBack={() => {
					handleGoTo(cancelRoute)
				}}
			/>
			{template?.config?.description && (
				<div style={{ padding: '0 24px' }}>
					<SuggestionAlert
						type="info"
						showIcon
						message="Informações do modelo"
						description={
							<ExpandableDescription text={template?.config?.description} />
						}
						style={{ whiteSpace: 'pre-line' }}
					/>
				</div>
			)}
			{!(isLastPage && hasPreview) && requesterData?.has_suggestion && (
				<div style={{ padding: '0 24px' }}>
					<SuggestionAlert
						message="Sugestão de Aprovador Nubank"
						description={`Com base na hierarquia do solicitante, o aprovador ideal é: ${requesterData.suggested_approver.name} - ${requesterData.suggested_approver.email}`}
						type="info"
						showIcon
						closable
					/>
				</div>
			)}
			<FormContent>
				<FormContentContainer isHidden={isLastPage && hasPreview}>
					<FormFactory
						initialValues={initialValues}
						form={form}
						setRequesterData={setRequesterData}
					/>
				</FormContentContainer>

				{isLastPage && hasPreview && (
					<FormContentContainer isPreview>
						<ContractPreview
							show={hasPreview}
							isExternal={isExternal}
							templateId={templateId}
						/>
					</FormContentContainer>
				)}
			</FormContent>
			<FormFooter>
				<FormButton
					disabled={!currentPage > 0}
					type="primary"
					onClick={onPrevious}>
					Anterior
				</FormButton>

				{lastPage > 0 && (
					<FormPagination>
						{currentStepper + 1} de {totalPages}
					</FormPagination>
				)}
				<FormButton disabled={isLastPage} type="primary" onClick={onNext}>
					Próximo
				</FormButton>
				{ExtraButtons()[1]}
			</FormFooter>
		</FormLayout>
	)
}

export default DocumentForm

DocumentForm.propTypes = {
	initialValues: object,
	onFinish: func,
	cancelRoute: string,
	renderPreview: func,
	allowDraft: bool,
	hasPreview: bool,
	isFinishing: bool,
	isExternal: bool,
	templateId: string,
}
